/**
 * Duration text for editable time-entry fields.
 *
 * Parsing and formatting here have to be exact inverses. The field is
 * pre-filled with the formatted value, so if the parser reads that value back
 * as a different number, focusing the field and tabbing away is enough to
 * rewrite the entry with time nobody worked.
 */

const MAX_SECONDS = 24 * 60 * 60;

/**
 * Parse a typed duration into whole seconds.
 *
 * Accepts `45` (minutes), `1:30` (h:mm), and `1:30:25` (h:mm:ss). A bare
 * number reads as minutes: that is the unit the quick-log buttons offer, and
 * "30" in a field showing `0:25:00` means half an hour to everyone who types
 * it. Returns null for anything it cannot read, which the caller treats as
 * "leave the entry alone".
 */
export function parseDurationSeconds(raw: string): number | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;

  if (/^\d+$/.test(trimmed)) return bounded(Number(trimmed) * 60);

  const match = trimmed.match(/^(\d+):([0-5]?\d)(?::([0-5]?\d))?$/);
  if (!match) return null;

  const [, hours, minutes, seconds] = match;
  return bounded(Number(hours) * 3600 + Number(minutes) * 60 + Number(seconds ?? 0));
}

function bounded(seconds: number): number | null {
  return seconds > 0 && seconds <= MAX_SECONDS ? seconds : null;
}

/**
 * Render seconds as `h:mm:ss`.
 *
 * Always leads with the hour, unlike the countdown clock: `25:00` is read back
 * as twenty-five hours, and an editable field cannot afford that ambiguity.
 */
export function formatDurationClock(totalSeconds: number): string {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const pad = (n: number) => String(n).padStart(2, '0');
  return `${hours}:${pad(minutes)}:${pad(seconds)}`;
}
